import { X } from 'lucide-react';
import { type MasterCategoryType } from '../../../types/masterSearchTypes';
import { useMasterSearch, type MasterSearchInitialValues } from '../../../hooks/useMasterSearch';
import { MasterSearchForm } from './MasterSearchForm';
import { MasterSearchDataTable } from './MasterSearchDataTable';

interface Props {
  isOpen: boolean;                          // ダイアログの表示フラグ
  onClose: () => void;                      // 閉じるボタンやキャンセル時のコールバック
  category: MasterCategoryType;             // 検索対象（SELLER/FARMER/BUYERなど）
  initialValues: MasterSearchInitialValues; // フォームの初期入力値
  onSelect: (selectedData: any) => void;    // 行決定時のコールバック
}

/**
 * マスタ検索ダイアログ（親コンポーネント）
 * 検索ロジックは useMasterSearch フックに切り出し、
 * フォーム、テーブル、フッターの各パーツを統合する
 */
export const MasterSearchDialog = ({
  isOpen,
  onClose,
  category,
  initialValues,
  onSelect,
}: Props) => {
  // --- 1. ロジック・状態管理 ---
  // カスタムフックから、検索に必要な状態とデータを一括で取得
  const {
    filters, setFilters,           // 一般検索条件
    sellerFilter, setSellerFilter, // 生産者検索時の売手フィルタ（Farmer専用）
    selectedRow, setSelectedRow,   // 現在の選択行
    processedData,                 // フィルタ・ソート済みの表示データ
    displayColumns,                // カテゴリに応じた動的なカラム定義
    isLoading,                     // API読込状態
    config,                        // マスタごとの設定（ラベル等）
    isFarmer                       // 生産者カテゴリかどうかの判定フラグ
  } = useMasterSearch(category, initialValues, isOpen);

  // ダイアログが閉じている場合、またはカラム定義が取得できていない場合はレンダリングしない
  if (!isOpen || displayColumns.length === 0) return null;

  // --- 2. ハンドラ ---
  /** 選択確定時の処理 */
  const handleConfirm = (row: any) => {
    if (!row) return; // 選択されていない場合は何もしない
    onSelect(row);    // 親コンポーネントへデータを渡す
    onClose();        // ダイアログを閉じる
  };

  return (
    // オーバーレイ層：画面全体を覆い、中央付近に内容を表示
    // スマホでは余白なし、PC(md)では p-8 で余白を持たせる
    <div 
      className="fixed inset-0 z-[110] bg-black/40 flex justify-center items-center md:p-8"
      onClick={(e) => e.stopPropagation()}
    >
      
      {/* ダイアログ本体：最大幅 4xl、高さは内容に応じて可変（内部スクロールあり） */}
      {/* スマホ(デフォルト): h-[100dvh] で全画面固定。フッターが最下部に吸着する。
          PC(md): h-auto でコンテンツに合わせつつ、max-h-[90vh] で画面を突き抜けないように制限。
      */}
      <div 
        className="bg-white shadow-2xl w-full max-w-4xl flex flex-col border border-slate-200 overflow-hidden h-[100dvh] md:h-auto md:max-h-[90vh] md:rounded-lg"
        onClick={(e) => e.stopPropagation()} // 本体内のクリックも念のため伝播を阻止
      >
        
        {/* --- セクションA：固定ヘッダー --- */}
        <div className="flex-none flex justify-between items-center px-6 py-3 border-b border-slate-100 bg-white">
          <h2 className="text-base font-bold text-slate-700">{(config as any)?.label || ''}検索</h2>
          <button 
            type="button"
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 p-1 transition-colors"
            aria-label="閉じる"
          >
            <X size={20} />
          </button>
        </div>

        {/* --- セクションB：検索フォーム --- 
            フィルタ状態と更新関数を渡し、クリア時のリセットロジックも集約 */}
        {/* flex-none を付与して、フォームがテーブルに押し潰されないように固定 */}
        <div className="flex-none">
          <MasterSearchForm 
            isFarmer={isFarmer}
            filters={filters}
            setFilters={setFilters}
            sellerFilter={sellerFilter}
            setSellerFilter={setSellerFilter}
            onSearch={() => setSelectedRow(null)} // 検索実行時は選択を解除
            onClear={() => {
              setFilters({ code: '', name: '' });
            }}
          />
        </div>

        {/* --- セクションC：データテーブル --- 
            計算済みのリストを表示。ダブルクリックでの即時決定もサポート */}
        {/* flex-1 と min-h-0 を指定。
            これにより、フォームが長くてもテーブルエリアが自動で縮み、
            テーブル内だけでスクロールが発生する。フッターは隠れない。 */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col border-b border-slate-100">
          <MasterSearchDataTable 
            data={processedData}
            columns={displayColumns}
            isLoading={isLoading}
            selectedRow={selectedRow}
            onRowClick={setSelectedRow}
            onRowDoubleClick={handleConfirm}
          />
        </div>

        {/* --- セクションD：フッター（操作エリア） --- */}
        {/* flex-none で高さを維持。bg-gray-50 で視認性を確保。 */}
        <div className="flex-none bg-gray-50 px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex justify-between items-center">
          {/* 件数表示 */}
          <span className="font-medium">全 {processedData.length} 件を表示</span>
          
          <div className="flex gap-2">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-4 py-1.5 text-slate-500 hover:bg-slate-100 rounded text-sm font-medium transition-colors"
            >
              キャンセル
            </button>
            <button 
              type="button" 
              disabled={!selectedRow} // 行が選択されていない時は決定不可
              onClick={() => handleConfirm(selectedRow)}
              className="px-8 py-1.5 bg-indigo-600 text-white rounded text-sm font-bold hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 shadow-sm transition-all"
            >
              決定
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};